import React, { useState } from 'react';
import { Heart, MessageCircle, Share, Send, MoreHorizontal, ThumbsUp, Eye, Bookmark } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { Textarea } from '../ui/textarea';
import { Separator } from '../ui/separator';

const PostCard = ({ post, onLike }) => {
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [comments, setComments] = useState([]);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [showFullContent, setShowFullContent] = useState(false);

  const handleComment = () => {
    if (!newComment.trim()) return;
    
    const comment = {
      id: Date.now(),
      author: 'You',
      content: newComment,
      timeAgo: 'now'
    };
    
    setComments([...comments, comment]);
    setNewComment('');
  };

  const formatNumber = (num) => {
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const handleBookmark = () => {
    setIsBookmarked(!isBookmarked);
  };

  const contentLines = post.content.split('\n');
  const shouldTruncate = contentLines.length > 3 || post.content.length > 200;
  const displayContent = shouldTruncate && !showFullContent 
    ? post.content.substring(0, 200) + '...'
    : post.content;

  return (
    <Card className="post-card card-hover shadow-soft hover:shadow-medium transition-all duration-300 border border-gray-100">
      <CardContent className="p-0">
        {/* Post Header */}
        <div className="p-4 pb-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3">
              <Avatar className="w-12 h-12 avatar-hover ring-2 ring-transparent hover:ring-blue-200 transition-all duration-300">
                <AvatarImage src={post.author.avatar} alt={post.author.name} />
                <AvatarFallback className="bg-gradient-to-br from-blue-500 to-blue-600 text-white font-semibold">
                  {post.author.name.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h4 className="font-semibold text-sm hover:text-blue-600 hover:underline cursor-pointer transition-colors duration-200">
                  {post.author.name}
                </h4>
                <p className="text-xs text-gray-600 mb-1">{post.author.title}</p>
                <div className="flex items-center space-x-1 text-xs text-gray-500">
                  <span>{post.timeAgo}</span>
                  <span>•</span>
                  <Eye className="w-3 h-3" />
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200"
                onClick={handleBookmark}
              >
                <Bookmark className={`w-4 h-4 transition-colors duration-200 ${isBookmarked ? 'fill-current text-blue-600' : ''}`} />
              </Button>
              <Button variant="ghost" size="sm" className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200">
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Post Content */}
        <div className="px-4 pb-3">
          <p className="text-sm leading-relaxed whitespace-pre-wrap text-gray-800">
            {displayContent}
          </p>
          {shouldTruncate && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowFullContent(!showFullContent)}
              className="text-blue-600 hover:text-blue-700 p-0 h-auto mt-2 font-medium transition-colors duration-200"
            >
              {showFullContent ? 'Show less' : 'Show more'}
            </Button>
          )}
        </div>

        {/* Post Media */}
        {post.image && (
          <div className="px-4 pb-3">
            <div className="relative overflow-hidden rounded-lg group cursor-pointer">
              <img 
                src={post.image} 
                alt="Post content" 
                className="w-full object-cover max-h-96 transition-transform duration-300 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
            </div>
          </div>
        )}

        {post.video && (
          <div className="px-4 pb-3">
            <div className="relative w-full h-64 bg-gray-100 rounded-lg overflow-hidden shadow-soft">
              <iframe
                src={post.video}
                title="Post video"
                className="w-full h-full"
                allowFullScreen
              />
            </div>
          </div>
        )}

        {/* Post Stats */}
        <div className="px-4 pb-2">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <div className="flex items-center space-x-4">
              {post.likes > 0 && (
                <div className="flex items-center space-x-1 hover:text-blue-600 cursor-pointer transition-colors duration-200">
                  <div className="flex -space-x-1">
                    <div className="w-4 h-4 bg-blue-600 rounded-full flex items-center justify-center shadow-soft">
                      <ThumbsUp className="w-2 h-2 text-white" />
                    </div>
                    <div className="w-4 h-4 bg-red-500 rounded-full flex items-center justify-center shadow-soft">
                      <Heart className="w-2 h-2 text-white" />
                    </div>
                  </div>
                  <span className="font-medium">{formatNumber(post.likes)}</span>
                </div>
              )}
            </div>
            <div className="flex items-center space-x-3">
              {post.comments > 0 && (
                <span className="hover:text-blue-600 cursor-pointer transition-colors duration-200">
                  {formatNumber(post.comments)} comments
                </span>
              )}
              {post.shares > 0 && (
                <span className="hover:text-blue-600 cursor-pointer transition-colors duration-200">
                  {formatNumber(post.shares)} shares
                </span>
              )}
            </div>
          </div>
        </div>

        <Separator />

        {/* Post Actions */}
        <div className="p-2">
          <div className="flex items-center justify-around">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onLike(post.id)}
              className={`flex-1 hover:bg-blue-50 transition-all duration-200 btn-hover group ${
                post.isLiked ? 'text-blue-600 bg-blue-50' : 'text-gray-600'
              }`}
            >
              <ThumbsUp className={`w-4 h-4 mr-2 transition-all duration-200 group-hover:scale-110 ${post.isLiked ? 'fill-current' : ''}`} />
              <span className="font-medium">Like</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowComments(!showComments)}
              className="flex-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 btn-hover group"
            >
              <MessageCircle className="w-4 h-4 mr-2 transition-transform duration-200 group-hover:scale-110" />
              <span className="font-medium">Comment</span>
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              className="flex-1 text-gray-600 hover:text-green-600 hover:bg-green-50 transition-all duration-200 btn-hover group"
            >
              <Share className="w-4 h-4 mr-2 transition-transform duration-200 group-hover:scale-110" />
              <span className="font-medium">Share</span>
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              className="flex-1 text-gray-600 hover:text-purple-600 hover:bg-purple-50 transition-all duration-200 btn-hover group"
            >
              <Send className="w-4 h-4 mr-2 transition-transform duration-200 group-hover:scale-110" />
              <span className="font-medium">Send</span>
            </Button>
          </div>
        </div>

        {/* Comments Section */}
        {showComments && (
          <>
            <Separator />
            <div className="p-4 space-y-4 bg-gray-50/50">
              {/* Add Comment */}
              <div className="flex items-start space-x-3">
                <Avatar className="w-8 h-8 ring-2 ring-transparent hover:ring-blue-200 transition-all duration-300">
                  <AvatarImage src="/api/placeholder/32/32" alt="You" />
                  <AvatarFallback className="bg-gradient-to-br from-blue-500 to-blue-600 text-white text-sm font-semibold">Y</AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <Textarea
                    placeholder="Write a comment..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    className="resize-none text-sm border-gray-200 focus:border-blue-300 focus:ring-2 focus:ring-blue-100 transition-all duration-200"
                    rows={2}
                  />
                  <div className="flex justify-end mt-2">
                    <Button
                      size="sm"
                      onClick={handleComment}
                      disabled={!newComment.trim()}
                      className="btn-hover bg-blue-600 hover:bg-blue-700 disabled:opacity-50 transition-all duration-200"
                    >
                      Post
                    </Button>
                  </div>
                </div>
              </div>

              {/* Comments List */}
              {comments.map((comment) => (
                <div key={comment.id} className="flex items-start space-x-3 animate-in slide-in-from-left-2 duration-300">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src="/api/placeholder/32/32" alt={comment.author} />
                    <AvatarFallback className="bg-gradient-to-br from-gray-500 to-gray-600 text-white text-sm">
                      {comment.author.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <div className="bg-white rounded-xl px-3 py-2 shadow-soft border border-gray-100">
                      <p className="font-semibold text-xs text-gray-800">{comment.author}</p>
                      <p className="text-sm text-gray-700 mt-1">{comment.content}</p>
                    </div>
                    <div className="flex items-center space-x-4 mt-1 text-xs text-gray-500">
                      <span>{comment.timeAgo}</span>
                      <button className="hover:text-blue-600 transition-colors duration-200 font-medium">Like</button>
                      <button className="hover:text-blue-600 transition-colors duration-200 font-medium">Reply</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default PostCard;